import type {
  APIBookingDraft,
  APIBookingDraftLuggageService,
  APIBookingDraftRoom,
  APIBookingDraftServiceLuggageFlightDefinition,
} from '@/scopes/checkout/infrastructure/api/types';
import {
  type CheckoutLuggageService,
  type CheckoutServiceOption,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types';

import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper';
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper';
import { isNil, values } from 'ramda';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceOptionMapper?: ServiceOptionMapper;
  serviceBannersMapper?: ServiceBannersMapper;
}

export const createLuggageServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutLuggageService> = (
  dependencies: Dependencies
) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies;

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftLuggageService;

    if (!service) return null;

    const { aName, options, hasSpecialLuggage, isOneClickLuggageReady, oneClickLuggageOptions } = service;

    const outboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'outbound');
    const inboundOptions = options.filter(({ direction }) => direction?.toLowerCase() === 'inbound');
    const participants =
      bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            id: participant.id.toString(),
            name: participant.name || participant.alternativeName,
            roomId: participant.roomId.toString(),
            selectedValueOutbound:
              options
                .filter(({ isSelected }) => isSelected)
                .filter(({ direction }) => direction?.toLowerCase() === 'outbound')
                .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))?.code || null,
            selectedValueInbound:
              options
                .filter(({ isSelected }) => isSelected)
                .filter(({ direction }) => direction?.toLowerCase() === 'inbound')
                .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id))?.code || null,
            availableOptions: options
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(({ code }) => code),
            availableOneClickOptions: oneClickLuggageOptions
              ?.filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(({ code }) => code),
          },
        }),
        {}
      ) ?? {};

    const anyParticipantHasSelectedValue = values(participants).some(
      ({ selectedValueOutbound }) => selectedValueOutbound
    );

    return {
      id: aName,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      type: ServiceType.Luggage,
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      outboundOptions:
        outboundOptions && outboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
          ? outboundOptions.reduce(
              (acc2: Record<string, CheckoutServiceOption>, option) => ({
                ...acc2,
                [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
              }),
              {}
            )
          : {},
      inboundOptions:
        inboundOptions && inboundOptions.filter(({ isNoneOption }) => !isNoneOption).length > 0
          ? inboundOptions.reduce(
              (acc2: Record<string, CheckoutServiceOption>, option) => ({
                ...acc2,
                [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
              }),
              {}
            )
          : {},
      isOneClickLuggageReady: isOneClickLuggageReady ?? false,
      oneClickSelectedValue: oneClickLuggageOptions?.find(({ isSelected }) => isSelected)?.code || null,
      oneClickLuggageOptions:
        isOneClickLuggageReady && oneClickLuggageOptions
          ? oneClickLuggageOptions
              .filter(({ isNoneOption }) => !isNoneOption)
              .reduce(
                (acc2: Record<string, CheckoutServiceOption>, option) => ({
                  ...acc2,
                  [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
                }),
                {}
              )
          : {},
      hasSpecialLuggage: hasSpecialLuggage ?? false,
      flightAirports: mapFlightAirports(service),
      flightsInformationBanner: mapFlightInformationBanner(service),
      banners: serviceBannersMapper.map(service),
    };
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map((participantId) => participantId.toString()),
    };
  }

  function mapFlightAirports({ luggageDefinitionInformation }: APIBookingDraftLuggageService) {
    if (!luggageDefinitionInformation) {
      return undefined;
    }
    const { outboundFlightDefinition, inboundFlightDefinition } = luggageDefinitionInformation;
    let text = '';
    outboundFlightDefinition
      ? (text += `${outboundFlightDefinition.airportNameDeparture} - ${outboundFlightDefinition.airportNameArrival}, ${outboundFlightDefinition.airlineName}`)
      : '';
    inboundFlightDefinition
      ? (text += ` | ${inboundFlightDefinition.airportNameDeparture} - ${inboundFlightDefinition.airportNameArrival}, ${inboundFlightDefinition.airlineName}`)
      : '';
    return text;
  }

  function mapFlightInformationBanner({ luggageDefinitionInformation }: APIBookingDraftLuggageService) {
    if (!luggageDefinitionInformation) {
      return undefined;
    }
    const { title, description, outboundFlightDefinition, inboundFlightDefinition } = luggageDefinitionInformation;

    const outboundFlight = mapFlightDefinition(outboundFlightDefinition);
    const inboundFlight = mapFlightDefinition(inboundFlightDefinition);

    return {
      ...(title && { title }),
      ...(description && { text: description }),
      ...(outboundFlight && { outboundFlight }),
      ...(inboundFlight && { inboundFlight }),
      type: 'info',
    };
  }

  function mapFlightDefinition(flightDefinition?: APIBookingDraftServiceLuggageFlightDefinition) {
    if (!flightDefinition) {
      return;
    }
    const { shouldBeShown, title, handLuggage, checkedInLuggage } = flightDefinition;

    if (!shouldBeShown) {
      return;
    }

    return {
      title,
      handLuggage,
      checkedInLuggage,
    };
  }

  function mapSelection(service: CheckoutLuggageService) {
    const { participants } = service;
    const participantsPayload = Object.values(participants ?? {})
      .map(({ id, selectedValueOutbound, selectedValueInbound }) => {
        if (service.oneClickSelectedValue) {
          return {
            participantId: id,
            value: service.oneClickSelectedValue,
          };
        }
        return [
          {
            participantId: id,
            value: selectedValueOutbound,
          },
          {
            participantId: id,
            value: selectedValueInbound,
          },
        ];
      })
      .flat();

    return [...participantsPayload, { hasSpecialLuggage: service.hasSpecialLuggage }];
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutLuggageService,
    previousService: CheckoutLuggageService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];

    if (!service) {
      return { ancillaryAddMapped, ancillaryRemoveMapped };
    }

    if (service.oneClickSelectedValue) {
      return mapOneClickLuggageAddAndRemoveResults(service, previousService);
    }

    return mapPerParticipantAddAndRemoveResults(service, previousService);
  }

  function mapPerParticipantAddAndRemoveResults(
    service: CheckoutLuggageService,
    previousService: CheckoutLuggageService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const participantModified = Object.keys(service?.participants).reduce(
      (modified: { id: string; previousValue: string | null; newValue: string | null }[], id) => {
        const prevParticipant = previousService?.participants[id];
        const newParticipant = service?.participants[id];

        const prevSelectedOutboundValue = prevParticipant?.selectedValueOutbound;
        const newSelectedOutboundValue = newParticipant?.selectedValueOutbound;

        if (prevSelectedOutboundValue !== newSelectedOutboundValue) {
          modified.push({
            id,
            previousValue: prevSelectedOutboundValue,
            newValue: newSelectedOutboundValue,
          });
        }

        const prevSelectedInboundValue = prevParticipant?.selectedValueInbound;
        const newSelectedInboundValue = newParticipant?.selectedValueInbound;

        if (prevSelectedInboundValue !== newSelectedInboundValue) {
          modified.push({
            id,
            previousValue: prevSelectedInboundValue,
            newValue: newSelectedInboundValue,
          });
        }

        return modified;
      },
      []
    );

    if (participantModified.length > 0) {
      participantModified.forEach((participant) => {
        if (participant.newValue) {
          const option = service.outboundOptions[participant.newValue] ?? service.inboundOptions[participant.newValue];
          const direction = service.outboundOptions[participant.newValue] ? 'Outbound' : 'Inbound';

          ancillaryAddMapped.push({
            id: participant.newValue,
            name: option?.label || '',
            price: option?.price || 0,
            category: `Ancillary > ${option?.badge ? 'Persuasion' : 'Additional cost'} > ${direction}`,
            quantity: 1,
            serviceType: direction,
            extraProperties: {},
          });
        }
        if (participant.previousValue) {
          const option =
            service.outboundOptions[participant.previousValue] ?? service.inboundOptions[participant.previousValue];
          const direction = service.outboundOptions[participant.previousValue] ? 'Outbound' : 'Inbound';

          ancillaryRemoveMapped.push({
            id: participant.previousValue,
            name: option?.label || '',
            price: option?.price || 0,
            category: `Ancillary > ${option?.badge ? 'Persuasion' : 'Additional cost'} > ${direction}`,
            quantity: 1,
            serviceType: direction,
            extraProperties: {},
          });
        }
      });
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  function mapOneClickLuggageAddAndRemoveResults(
    service: CheckoutLuggageService,
    previousService: CheckoutLuggageService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];

    const direction = 'Both';

    if (service.oneClickSelectedValue) {
      const optionClicked = service.oneClickLuggageOptions[service?.oneClickSelectedValue];
      ancillaryAddMapped.push({
        id: optionClicked.value,
        name: optionClicked.label,
        price: optionClicked.price,
        category: `Ancillary > ${optionClicked.badge ? 'Persuasion' : 'Additional cost'} > ${direction}`,
        quantity: values(service?.participants).length,
        serviceType: direction,
        extraProperties: {},
      });
    }

    if (previousService.oneClickSelectedValue) {
      const previousOptionClicked = previousService.oneClickLuggageOptions[previousService?.oneClickSelectedValue];

      ancillaryRemoveMapped.push({
        id: previousOptionClicked?.value,
        name: previousOptionClicked?.label,
        price: previousOptionClicked?.price,
        category: `Ancillary > ${previousOptionClicked?.badge ? 'Persuasion' : 'Additional cost'} > ${direction}`,
        quantity: values(previousService?.participants).length,
        serviceType: direction,
        extraProperties: {},
      });
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
